import type { CompiledConstraint, CompiledRule, CompiledStage } from "../terrain/compile-rules.ts";
import { skipped, type CellRect, type StageTally, type WorkingMap } from "../terrain/match.ts";

/**
 * The rerun of a `MatchInOrder` true stage after the layer it reads changed in a rectangle (ADR-020's
 * amendment of 2026-10-05, decision 1). The stage's scan is rule-major: every rule in turn is tried at every
 * position, row by row, and applies at once, so a later rule anywhere on the map reads what an earlier rule
 * wrote anywhere, and a session keeps only the layer before the stage and the layer after it, never the layer
 * between two rules. A rerun is therefore right only where it replays, from the layer before the stage,
 * every step (a rule at a position) that decides a cell.
 *
 * The rerun replays the steps of a window of positions around the change twice at once, the run the session
 * holds (over the pre-stage layer as it was) and the new run (over the layer as it is), and proves as it goes
 * that every step outside the window does in the new run what it did in the old one:
 *
 * - A cell a step outside the window can write is `OUTSIDE`: its value between rules is not known here. A
 *   step in the window whose match hangs on such a cell makes the cells it writes `UNKNOWN` too. By induction
 *   over the scan an unknown cell holds the same value in both runs at every step, which is all a step
 *   outside the window needs in order to repeat itself.
 * - A cell that is known holds its exact value in each run, and the two differ only where the change has
 *   reached.
 * - A step that fails in both runs on known cells is passed over, which is what almost every step does.
 *
 * The proof fails, and the window grows, when a step could leave an unknown cell different in the two runs:
 * its match hangs on an unknown cell while the known cells let only one run match, or while it would write
 * over a cell that differs, or it matches in one run alone and writes an unknown cell. A window that covers
 * the map has no step outside it and no unknown cell, and is a whole run of the stage; a window grown to
 * more than `WHOLE_RUN_SHARE` of the map's positions is left to the caller to run whole. On success the
 * known cells hold the new run's values and every other cell holds what it held.
 *
 * The replay knows one layer, the stage's own. A stage is replayed only when every constraint of its active
 * rules reads that layer, inside the rule's input box: a rule may read another layer that an earlier rule of
 * the same stage wrote, and such a stage is run whole by the caller.
 */

const UNKNOWN = 1;
const OUTSIDE = 2;
const HASH_INPUTS = 5;
const GROWTH = 2;

/**
 * The share of the map's positions a window may grow to. A window grown past it is not replayed: the caller
 * runs the stage whole, which costs less than a window of that size and the wider ones after it. The first
 * window, the change's own reach, is replayed whatever its size, since a large change that does not cascade
 * succeeds there for less than a whole run; either way the result is a whole run's, the share only choosing
 * between two exact ways to it.
 */
export const WHOLE_RUN_SHARE = 0.5;

/**
 * The cells of a layer as they were inside a rectangle before an update changed them, row-major over `area`.
 */
export interface PriorCells {
  readonly area: CellRect;
  readonly values: Uint32Array;
}

/**
 * What a rerun reads and writes.
 */
export interface OrderedRerun {
  readonly stage: CompiledStage;
  readonly target: string;
  readonly extent: { readonly minDx: number; readonly minDy: number; readonly maxDx: number; readonly maxDy: number };
  readonly width: number;
  readonly height: number;
  readonly seed: number;
  readonly layerIndex: number;
  readonly stageIndex: number;
  readonly previous: Uint32Array;
  readonly prior: PriorCells;
  readonly current: Uint32Array;
  readonly tally: StageTally;
}

/**
 * One attempt's window: the positions replayed, the cells they can touch, and over those cells, row-major,
 * each run's values and the flags.
 */
interface Attempt {
  readonly positions: CellRect;
  readonly cells: CellRect;
  readonly span: number;
  readonly origin: number;
  readonly old: Uint32Array;
  readonly fresh: Uint32Array;
  readonly flags: Uint8Array;
}

/**
 * Whether a value passes a constraint.
 *
 * @param constraint The constraint.
 * @param value The cell's value.
 * @returns True when the value is accepted and not rejected.
 */
function passes(constraint: CompiledConstraint, value: number): boolean {
  const { anyOf, noneOf } = constraint;
  if (anyOf.length > 0) {
    let accepted = false;
    for (let i = 0; i < anyOf.length; i += 1) {
      if (anyOf[i] === value) {
        accepted = true;
        break;
      }
    }
    if (!accepted) {
      return false;
    }
  }
  for (let i = 0; i < noneOf.length; i += 1) {
    if (noneOf[i] === value) {
      return false;
    }
  }
  return true;
}

/**
 * Sets up an attempt over a window of positions: both runs start from the pre-stage layer over the cells
 * the window can touch, the old run with the cells as they were, and the cells a step outside the window can
 * write are marked.
 *
 * @param run The rerun.
 * @param positions The window of positions, inside the map.
 * @returns The attempt.
 */
function begin(run: OrderedRerun, positions: CellRect): Attempt {
  const { extent, width, height, previous, prior } = run;
  const cells: CellRect = { x0: Math.max(0, positions.x0 + extent.minDx), y0: Math.max(0, positions.y0 + extent.minDy), x1: Math.min(width, positions.x1 - 1 + extent.maxDx), y1: Math.min(height, positions.y1 - 1 + extent.maxDy) };
  const span = cells.x1 - cells.x0;
  const size = span * (cells.y1 - cells.y0);
  const fresh = new Uint32Array(size);
  const flags = new Uint8Array(size);
  const left = positions.x0 > 0 ? positions.x0 + extent.maxDx - 1 : cells.x0;
  const right = positions.x1 < width ? positions.x1 + extent.minDx : cells.x1;
  const top = positions.y0 > 0 ? positions.y0 + extent.maxDy - 1 : cells.y0;
  const bottom = positions.y1 < height ? positions.y1 + extent.minDy : cells.y1;
  for (let y = cells.y0; y < cells.y1; y += 1) {
    const base = (y - cells.y0) * span;
    fresh.set(previous.subarray(y * width + cells.x0, y * width + cells.x1), base);
    if (y < top || y >= bottom) {
      flags.fill(OUTSIDE | UNKNOWN, base, base + span);
    } else {
      flags.fill(OUTSIDE | UNKNOWN, base, base + Math.max(0, left - cells.x0));
      flags.fill(OUTSIDE | UNKNOWN, base + Math.min(span, right - cells.x0), base + span);
    }
  }
  const old = new Uint32Array(fresh);
  const { area, values } = prior;
  const areaSpan = area.x1 - area.x0;
  const x0 = Math.max(area.x0, cells.x0);
  const x1 = Math.min(area.x1, cells.x1);
  for (let y = Math.max(area.y0, cells.y0); y < Math.min(area.y1, cells.y1) && x1 > x0; y += 1) {
    const from = (y - area.y0) * areaSpan - area.x0;
    old.set(values.subarray(from + x0, from + x1), (y - cells.y0) * span + x0 - cells.x0);
  }
  return { positions, cells, span, origin: cells.y0 * span + cells.x0, old, fresh, flags };
}

/**
 * Replays one step, a rule at a position, in both runs.
 *
 * @param run The rerun.
 * @param attempt The attempt.
 * @param rule The rule.
 * @param px Position column.
 * @param py Position row.
 * @param map The stage's seed and indices, for the draw.
 * @param scratch Preallocated hash input buffer.
 * @returns False when the step could leave an unknown cell different in the two runs, so the window must grow.
 */
function step(run: OrderedRerun, attempt: Attempt, rule: CompiledRule, px: number, py: number, map: WorkingMap, scratch: Uint32Array): boolean {
  const { width, height, target } = run;
  const { flags, old, fresh, span, origin } = attempt;
  let unknown = false;
  let inOld = true;
  let inFresh = true;
  for (const side of rule.targets) {
    for (const constraint of side.constraints) {
      const at = (py + constraint.dy) * span + px + constraint.dx - origin;
      if (((flags[at] as number) & UNKNOWN) !== 0) {
        unknown = true;
        continue;
      }
      const was = old[at] as number;
      const is = fresh[at] as number;
      const isPasses = passes(constraint, is);
      inFresh = inFresh && isPasses;
      inOld = inOld && (was === is ? isPasses : passes(constraint, was));
      if (!inOld && !inFresh) {
        return true;
      }
    }
  }
  if (skipped(rule, px, py, map, scratch, run.tally)) {
    return true;
  }
  if (unknown && inOld !== inFresh) {
    return false;
  }
  for (const output of rule.outputs) {
    if (output.target !== target) {
      continue;
    }
    for (const cell of output.cells) {
      const x = px + cell.dx;
      const y = py + cell.dy;
      if (x < 0 || y < 0 || x >= width || y >= height) {
        continue;
      }
      const at = y * span + x - origin;
      const flag = flags[at] as number;
      if (unknown) {
        if ((flag & UNKNOWN) === 0 && old[at] !== fresh[at]) {
          return false;
        }
        flags[at] = flag | UNKNOWN;
      } else if (inOld && inFresh) {
        old[at] = cell.value;
        fresh[at] = cell.value;
        flags[at] = (flag & OUTSIDE) !== 0 ? flag : 0;
      } else if ((flag & UNKNOWN) !== 0) {
        return false;
      } else if (inOld) {
        old[at] = cell.value;
      } else {
        fresh[at] = cell.value;
      }
    }
  }
  return true;
}

/**
 * Replays the stage over an attempt's window of positions.
 *
 * @param run The rerun.
 * @param attempt The attempt.
 * @returns False when the window must grow.
 */
function replay(run: OrderedRerun, attempt: Attempt): boolean {
  const scratch = new Uint32Array(HASH_INPUTS);
  const map: WorkingMap = { width: run.width, height: run.height, layers: new Map(), seed: run.seed, layerIndex: run.layerIndex, stageIndex: run.stageIndex };
  const { positions } = attempt;
  for (const rule of run.stage.rules) {
    if (rule.disabled || rule.impossible) {
      continue;
    }
    const xEnd = Math.min(positions.x1, run.width - rule.inputWidth + 1);
    const yEnd = Math.min(positions.y1, run.height - rule.inputHeight + 1);
    run.tally.tested += Math.max(0, xEnd - positions.x0) * Math.max(0, yEnd - positions.y0);
    for (let py = positions.y0; py < yEnd; py += 1) {
      for (let px = positions.x0; px < xEnd; px += 1) {
        if (!step(run, attempt, rule, px, py, map, scratch)) {
          return false;
        }
      }
    }
  }
  return true;
}

/**
 * Copies an attempt's known cells into the stage's layer, keeping what the layer held.
 *
 * @param run The rerun.
 * @param attempt The attempt that succeeded.
 * @returns The bounds of the cells whose value changed, empty (`x1 <= x0`) when none did, and the layer's
 *   cells over the attempt's cells as they were.
 */
function settle(run: OrderedRerun, attempt: Attempt): { readonly changed: CellRect; readonly prior: PriorCells } {
  const { current, width } = run;
  const { cells, flags, fresh, span } = attempt;
  const changed: CellRect = { x0: width, y0: cells.y1, x1: 0, y1: cells.y0 };
  const values = new Uint32Array(fresh.length);
  for (let y = cells.y0; y < cells.y1; y += 1) {
    const base = (y - cells.y0) * span - cells.x0;
    values.set(current.subarray(y * width + cells.x0, y * width + cells.x1), base + cells.x0);
    for (let x = cells.x0; x < cells.x1; x += 1) {
      const i = y * width + x;
      if (((flags[base + x] as number) & UNKNOWN) === 0 && current[i] !== fresh[base + x]) {
        current[i] = fresh[base + x] as number;
        changed.x0 = Math.min(changed.x0, x);
        changed.x1 = Math.max(changed.x1, x + 1);
        changed.y0 = Math.min(changed.y0, y);
        changed.y1 = Math.max(changed.y1, y + 1);
      }
    }
  }
  return { changed, prior: { area: cells, values } };
}

/**
 * Brings an ordered stage's layer up to date after the layer it reads changed inside a rectangle. `current`
 * ends cell for cell what a whole run of the stage over `previous` leaves. Every constraint of the stage's
 * active rules must read the stage's own layer inside its rule's input box.
 *
 * @param run The stage, its extent, the map's size, the seed and indices of its draws, the layer the stage
 *   reads (`previous`, already changed) with its cells as they were (`prior`, covering every cell that
 *   changed), the layer the stage left (`current`) and the tally grown by the positions tried and the draws
 *   made.
 * @param dirty Cells of `previous` that changed, inside the map and not empty.
 * @returns The bounds of the cells of `current` that changed, empty (`x1 <= x0`) when none did, and those
 *   cells as they were, for the stage that reads `current`; or null, with `current` untouched, when the
 *   first window failed and the next would pass `WHOLE_RUN_SHARE` of the map, so the stage is to be run whole.
 */
export function rerunOrderedStage(run: OrderedRerun, dirty: CellRect): { readonly changed: CellRect; readonly prior: PriorCells } | null {
  const { extent, width, height } = run;
  let reach = 0;
  for (;;) {
    const positions: CellRect = { x0: Math.max(0, dirty.x0 - extent.maxDx + 1 - reach), y0: Math.max(0, dirty.y0 - extent.maxDy + 1 - reach), x1: Math.min(width, dirty.x1 - extent.minDx + reach), y1: Math.min(height, dirty.y1 - extent.minDy + reach) };
    if (reach > 0 && (positions.x1 - positions.x0) * (positions.y1 - positions.y0) > WHOLE_RUN_SHARE * width * height) {
      return null;
    }
    const attempt = begin(run, positions);
    if (replay(run, attempt)) {
      return settle(run, attempt);
    }
    reach = reach === 0 ? GROWTH : reach * GROWTH;
  }
}
